import { Button, EmptyState, Spinner } from '@freightapi/ui'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { ApiError, apiBaseUrl, fetchWelcome, type WelcomePayload } from '../lib/api'
import { useConsoleI18n } from '../lib/locale'

/**
 * Where the magic link lands after signup. The `sk_test_` key is read here and nowhere else: the
 * API hands it out exactly once and deletes it, so a reload shows the "already shown" state rather
 * than the secret (ADR 0005). `sk_live_` is not offered at all — live mode is a manual switch by
 * Cargofollow, which is what the "request live" block explains.
 */
export const Route = createFileRoute('/welcome')({ component: Welcome })

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly welcome: WelcomePayload }
  | { readonly status: 'error'; readonly message: string }

function Welcome() {
  const { t } = useConsoleI18n()
  const [state, setState] = useState<State>({ status: 'loading' })

  // Fetched exactly once per mount, and deliberately without an AbortController: reading the key
  // destroys it server-side, so a second call — React's development double-effect above all —
  // would answer `null` and hide the very secret this screen exists to show.
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true

    fetchWelcome()
      .then((welcome) => setState({ status: 'ready', welcome }))
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.isUnauthenticated) {
          window.location.assign('/login')
          return
        }
        setState({
          status: 'error',
          message: cause instanceof ApiError ? cause.message : 'error',
        })
      })
  }, [])

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }

  if (state.status === 'error') {
    return <EmptyState title={t('error.title')} description={t('error.body')} />
  }

  const { organization, api_key: apiKey } = state.welcome

  return (
    <section className="mx-auto flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold text-display text-ink">{t('welcome.title')}</h1>
        <p className="text-body text-muted">
          {t('welcome.intro', { organization: organization.name })}
        </p>
      </div>

      {apiKey === null ? (
        <EmptyState title={t('welcome.key.gone.title')} description={t('welcome.key.gone.body')} />
      ) : (
        <ApiKeyCard value={apiKey.key} />
      )}

      <div className="flex flex-col gap-2">
        <h2 className="font-semibold text-ink text-lead">{t('welcome.quickstart.title')}</h2>
        <p className="text-body text-muted">{t('welcome.quickstart.body')}</p>
        <pre className="code-block rounded-card bg-surface p-4 text-caption text-ink">
          {quickstart(apiKey?.key ?? 'sk_test_…')}
        </pre>
      </div>

      <div className="flex flex-col gap-2 rounded-card border border-line p-4">
        <h2 className="font-semibold text-ink text-lead">{t('live.title')}</h2>
        <p className="text-body text-muted">{t('live.body')}</p>
        <Link to="/conformance" className="text-body text-brand-700 underline">
          {t('live.request')}
        </Link>
      </div>

      <Link to="/" className="text-body text-brand-700 underline">
        {t('welcome.continue')}
      </Link>
    </section>
  )
}

/** The key, once. Copying is offered because selecting 50 monospace characters by hand is a trap. */
function ApiKeyCard({ value }: { readonly value: string }) {
  const { t } = useConsoleI18n()
  const [copied, setCopied] = useState(false)

  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4">
      <h2 className="font-semibold text-ink text-lead">{t('welcome.key.label')}</h2>
      <code data-testid="api-key" className="code-block text-body text-ink">
        {value}
      </code>
      <p className="text-caption text-muted">{t('welcome.key.once')}</p>
      <Button
        variant="secondary"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => setCopied(true))
        }}
      >
        {copied ? t('welcome.key.copied') : t('welcome.key.copy')}
      </Button>
    </div>
  )
}

function quickstart(key: string): string {
  return [
    `curl -X POST ${apiBaseUrl()}/v1/shipments \\`,
    `  -H "Authorization: Bearer ${key}" \\`,
    '  -H "Idempotency-Key: $(uuidgen)" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '{"reference":"DEMO-0001","mode":"test"}'`,
  ].join('\n')
}
